import { useEffect, useRef, useState } from 'react';

type Props<Type> = {
  items: Type[];
  initialDefaultItem: Type;
  itemComparator: (a: Type, b: Type) => boolean;
  pageSize: number;
};

const usePagination = <Type,>({
  items,
  pageSize,
  initialDefaultItem,
  itemComparator,
}: Props<Type>) => {
  const [current, setCurrent] = useState<number>(1);
  const hasPositionedInitialItem = useRef(false);

  const itemsCount = items.length;
  const pageCount = Math.ceil(itemsCount / pageSize);

  const pageStart = (current - 1) * pageSize;
  const pageEnd =
    pageCount !== current ? current * pageSize - 1 : items.length - 1;

  const selectPage = (page: number) => {
    if (page > pageCount) return setCurrent(Math.max(pageCount, 1));
    if (page < 1) return setCurrent(1);
    setCurrent(page);
  };

  const currentPageItems = items.filter(
    (_, index) => pageStart <= index && index <= pageEnd
  );

  useEffect(() => {
    if (hasPositionedInitialItem.current) return;

    const selectedIndex = items.findIndex((i) =>
      itemComparator(i, initialDefaultItem)
    );
    if (selectedIndex > -1) {
      const pageToSet = Math.ceil((selectedIndex + 1) / pageSize);
      hasPositionedInitialItem.current = true;
      if (pageToSet < 1) {
        return setCurrent(1);
      }
      setCurrent(pageToSet);
    }
  }, [initialDefaultItem, itemComparator, items, pageSize]);

  return { pageCount, current, selectPage, currentPageItems };
};

export { usePagination };
